/** Open/closed state for a popup anchored to a trigger, with the two behaviours every one needs:
 * a pointer press outside closes it, and closing by keyboard hands focus back to the trigger.
 *
 * Escape and arrow keys belong to the popup's own `onKeyDown` (they differ by pattern: a listbox, a
 * menu); this hook only owns the parts that are the same everywhere.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export function usePopup<T extends HTMLElement = HTMLButtonElement>() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<T>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPress = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPress);
    return () => document.removeEventListener("pointerdown", onPress);
  }, [open]);

  /** Close and, when asked, put focus back on the trigger (keyboard closes do; outside presses do not). */
  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  return { open, setOpen, close, triggerRef, popupRef };
}
